// Whether the cart can be had, asked of the server a moment after the buyer
// stops changing it — the +/- of the cards, the cart rail and the cart, a
// product added from a list. One request for the whole cart
// (store {cart_availability}: the stock checkout reserves from, the units
// other checkouts hold taken off, a size its own stock, an outfit its parts,
// a licence its free keys, the seller's limit), however many clicks led to it.
//
// The answer is kept under CART_VALIDATION_KEY for the cart (a message under
// each short line, checkout blocked meanwhile) and for the "+" buttons (off
// at the line's cap). A line the buyer just pushed past what there is comes
// back down to it, with a toast saying why.
import { getValue, setValue } from "aldoo-ra/Value"
import { store } from "aldoo-ra/Store"
import { showErrorToast } from "aldoo-ra/Toast"
import { makeT, getTable } from "../hooks/useT"
import { lineKey, toCheckoutCart, hasPlatformSelection, lineProduct } from "../checkout/cartItems"
import { CartAvailability } from "../storeApi"

export const CART_VALIDATION_KEY = "sf-cart-validation"
const CART_KEY = "aldoo-store-cart"
const DELAY = 400

let timer = null
let notify = new Set()
let running = false
let again = false

const cartNow = () => getValue(CART_KEY) || []
const signatureOf = (cart) => JSON.stringify(cart.map((line) => [lineKey(line), line.quantity, line.options?.platform]))
const tNow = () => makeT(getTable(getValue("current-site-language") || "en"))

/**
 * Asks again in a moment. `touched`: the keys of the lines the buyer just
 * changed — a shortage on one of them is said in a toast (the others only
 * under their line in the cart).
 */
export function scheduleStockCheck(touched = []) {
  if (typeof window === "undefined") return
  for (const key of touched) if (key) notify.add(key)
  clearTimeout(timer)
  timer = setTimeout(run, DELAY)
}

/** What the last answer says of one line: { valid, message, cap } or null. */
export const lineCheckOf = (validation, line) => validation?.items?.find((item) => item.key === lineKey(line)) || null

/** The message of a short line, in the buyer's words. */
function messageOf(answer, cap, line, t) {
  if (answer.reason === "not_found" || answer.reason === "unavailable") return t`This item is no longer available`
  if (answer.reason === "insufficient_keys") return t`Not enough licenses in stock`
  if (answer.reason && cap === 0) return t`This item is out of stock`
  if (answer.reason && Number.isFinite(answer.available)) return t("Only %stock% available", answer.available)
  if (answer.reason) return t`This item is out of stock`
  // within the stock, over the seller's limit
  if (answer.max) return answer.contact ? t("You can order up to %s of this item at once. For more, write to %s.", answer.max, answer.contact) : t("You can order up to %s of this item at once.", answer.max)
  return null
}

async function run() {
  if (running) {
    again = true
    return
  }
  const cart = cartNow()
  const touched = notify
  notify = new Set()
  if (!cart.length) {
    setValue(CART_VALIDATION_KEY, { valid: true, items: [], invalidCount: 0 })
    return
  }
  running = true
  const asked = signatureOf(cart)
  let answer = null
  try {
    answer = await CartAvailability(toCheckoutCart(cart).map((item, index) => ({ ...item, lineKey: lineKey(cart[index]) })))
  } catch {
    answer = null
  }
  running = false
  // the cart moved while the server was answering: that answer is of a cart
  // that is gone — ask again, keeping the lines to speak for
  if (again || signatureOf(cartNow()) !== asked) {
    again = false
    touched.forEach((key) => notify.add(key))
    return scheduleStockCheck()
  }
  // no answer (offline, an error): nothing is held back, checkout asks anyway
  if (!answer?.lines) {
    setValue(CART_VALIDATION_KEY, { valid: true, items: cart.map((line) => ({ key: lineKey(line), pid: line.pid, name: line.name, valid: true, message: null, available: null, cap: null, quantity: line.quantity || 1 })), invalidCount: 0 })
    return
  }

  const t = tNow()
  // the seller's limit counts every line of a product
  const ofProduct = (pid) => cart.filter((line) => line.pid === pid).reduce((n, line) => n + (line.quantity || 1), 0)
  const items = cart.map((line, index) => {
    const result = answer.lines[index] || {}
    const quantity = line.quantity || 1
    const others = ofProduct(line.pid) - quantity
    const byStock = Number.isFinite(result.available) ? Math.max(0, result.available) : Infinity
    const byLimit = Number(result.max) > 0 ? Math.max(0, result.max - others) : Infinity
    const cap = Math.min(byStock, byLimit)
    const over = quantity > cap
    const valid = result.valid !== false && !over
    const message = valid ? null : messageOf(over && byLimit < byStock && result.valid !== false ? { ...result, reason: null } : result, cap, line, t)
    return { key: lineKey(line), pid: line.pid, name: line.name, valid, message, available: result.available ?? null, cap: Number.isFinite(cap) ? cap : null, quantity, license: hasPlatformSelection(lineProduct(line)) }
  })

  // a line pushed past what there is comes down to it (a licence keeps its
  // count: its platforms are chosen in their own dialog); one at nothing stays,
  // marked, for the buyer to take out
  const clamped = []
  for (const item of items) {
    if (item.license || item.cap === null || item.cap < 1 || item.quantity <= item.cap) continue
    const line = cart.find((entry) => lineKey(entry) === item.key)
    store.setLine(item.key, { quantity: item.cap, options: line?.options })
    clamped.push(item)
  }
  // a line kept in the browser since before its seller was renamed takes the name it has now, so the
  // cart groups it with that seller's other lines (and its free delivery counts it)
  for (const [index, line] of cart.entries()) {
    const seller = answer.lines[index]?.seller
    if (!seller || line.options?.seller?.name === seller) continue
    store.setItemOptions(lineKey(line), { seller: { ...(line.options?.seller || {}), name: seller } })
  }

  // what the cart now holds is what the answer was checked for
  const settled = items.map((item) => (clamped.includes(item) ? { ...item, quantity: item.cap, valid: true } : item))
  const invalidCount = settled.filter((item) => !item.valid).length
  setValue(CART_VALIDATION_KEY, { valid: invalidCount === 0, items: settled, invalidCount })

  // said once per line the buyer touched: why it stopped, or why it cannot be had
  for (const item of items) {
    if (item.valid || !touched.has(item.key) || !item.message) continue
    showErrorToast(item.name || "", item.message, { duration: 3500, enableDismissalTracking: false })
  }
}
